import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import apiService from '../../services/api';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  PackageOpen, 
  Briefcase,
  ClipboardList,
  Settings, 
  LogOut, 
  Menu, 
  X,
  UserCircle,
  Tag,
  Layers,
  Grid,
  Shapes,
  Cpu,
  Globe,
  Ruler,
  Percent,
  ChevronDown,
  ChevronRight,
  Calendar,
  TrendingUp,
  Ship,
  FileCheck,
  StickyNote,
  AlignLeft,
  Bell
} from 'lucide-react';

// Role-based access: quotation cannot access Users, Settings, BOQ; boq cannot access Users, Settings, Quotations
const canAccessPath = (role: string | undefined, path: string): boolean => {
  if (!role || role === 'admin' || role === 'manager' || role === 'user' || role === 'staff') return true;
  if (role === 'quotation') {
    if (path === '/users' || path.startsWith('/users/')) return false;
    if (path === '/settings' || path.startsWith('/settings/')) return false;
    if (path === '/boqs' || path.startsWith('/boqs/')) return false;
    return true;
  }
  if (role === 'boq') {
    if (path === '/users' || path.startsWith('/users/')) return false;
    if (path === '/settings' || path.startsWith('/settings/')) return false;
    if (path === '/quotations' || path.startsWith('/quotations/')) return false;
    return true;
  }
  return true;
};

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [notifications, setNotifications] = useState<{ _id: string; type?: string; title: string; message?: string; read: boolean; relatedType?: string; relatedId?: string; createdAt: string }[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  const fetchNotifications = () => {
    apiService.getNotifications()
      .then((res) => {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount ?? 0);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Request device notification permission when user is logged in
  useEffect(() => {
    if (!user || typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [user]);

  // Real-time notifications via Socket.io + device notifications
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const socketUrl = apiUrl.replace(/\/api\/?$/, '') || window.location.origin;
    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;
    socket.on('notification', (payload: { _id: string; type?: string; title: string; message?: string; read: boolean; relatedType?: string; relatedId?: string; createdAt: string }) => {
      const doc = { ...payload, createdAt: payload.createdAt || new Date().toISOString() };
      setNotifications((prev) => [doc, ...prev]);
      setUnreadCount((c) => c + 1);
      toast(
        (t) => (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-gray-900">{doc.title}</span>
            {doc.message && <span className="text-sm text-gray-600">{doc.message}</span>}
          </div>
        ),
        {
          duration: 6000,
          icon: '🔔',
          style: {
            background: '#9CA3AF',
            color: '#1F2937',
            fontFamily: 'var(--font-family)',
          },
        }
      );
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        const deviceNotif = new Notification(doc.title, {
          body: doc.message || '',
          icon: '/logo192.png',
          tag: doc._id,
          requireInteraction: false,
        });
        deviceNotif.onclick = () => {
          window.focus();
          deviceNotif.close();
          if (doc.relatedType === 'boq' && doc.relatedId) {
            navigate(`/boqs/${doc.relatedId}`);
          } else if (doc.relatedType === 'quotation' && doc.relatedId) {
            navigate(`/quotations/${doc.relatedId}`);
          }
        };
      }
    });
    socket.on('connect_error', () => {
      // Optional: fallback to polling only if websocket fails
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, navigate]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notificationsOpen]);

  const handleNotificationClick = (n: { _id: string; read: boolean; relatedType?: string; relatedId?: string }) => {
    if (!n.read) {
      apiService.markNotificationRead(n._id).then(() => {
        setNotifications((prev) => prev.map((x) => (x._id === n._id ? { ...x, read: true } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
      }).catch(() => {});
    }
    setNotificationsOpen(false);
    if (n.relatedType === 'boq' && n.relatedId) {
      navigate(`/boqs/${n.relatedId}`);
    } else if (n.relatedType === 'quotation' && n.relatedId) {
      navigate(`/quotations/${n.relatedId}`);
    }
  };

  const markAllRead = () => {
    apiService.markAllNotificationsRead()
      .then(() => {
        setNotifications((prev) => prev.map((x) => ({ ...x, read: true })));
        setUnreadCount(0);
      })
      .catch(() => {});
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Quotations', href: '/quotations', icon: FileText },
    { name: 'BOQ', href: '/boqs', icon: ClipboardList },
    { name: 'Customers', href: '/customers', icon: Users },
    { name: 'Catalog', href: '/quotation-items', icon: PackageOpen },
    { name: 'Jobs', href: '/jobs', icon: Briefcase },
    { name: 'Users', href: '/users', icon: UserCircle },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const navigation = allNavigation.filter((item) => canAccessPath(user?.role, item.href));

  // Redirect to dashboard if current path is not allowed for this role
  useEffect(() => {
    if (user?.role && !canAccessPath(user.role, location.pathname)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  const masterDataItems = [
    { name: 'Brands', href: '/brands', icon: Tag },
    { name: 'Sections', href: '/sections', icon: Layers },
    { name: 'Categories', href: '/categories', icon: Grid },
    { name: 'Types', href: '/types', icon: Shapes },
    { name: 'Compressor Types', href: '/compressor-types', icon: Cpu },
    { name: 'Countries', href: '/countries', icon: Globe },
    { name: 'Units', href: '/units', icon: Ruler },
    { name: 'VAT Rates', href: '/vats', icon: Percent },
    { name: 'AIT Rates', href: '/aits', icon: Percent },
    { name: 'Schedules', href: '/schedules', icon: Calendar },
    { name: 'Profit Margins', href: '/profit-margins', icon: TrendingUp },
    { name: 'Customs Duty & Freight', href: '/customs-duties', icon: Ship },
    { name: 'Terms & Conditions', href: '/terms-and-conditions', icon: FileCheck },
    { name: 'Notes', href: '/notes', icon: StickyNote },
    { name: 'Footnote', href: '/footnotes', icon: AlignLeft },
    { name: 'Employees', href: '/employees', icon: UserCircle },
  ];

  const isActive = (path: string) => location.pathname.startsWith(path);

  const companyName = settings?.companyName || 'The Aircons Ltd';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 md:z-50">
        <div className="flex-1 flex flex-col min-h-0 bg-gradient-to-b from-[var(--color-primary)] to-[var(--color-secondary)] shadow-xl">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            {/* Logo/Company Name */}
            <div className="flex items-center flex-shrink-0 px-4 mb-8">
              <motion.h1 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-bold text-white" 
                style={{ fontFamily: 'var(--font-family)' }}
              >
                {companyName}
              </motion.h1>
            </div>
            
            {/* Navigation Links */}
            <nav className="flex-1 px-3 space-y-2">
              {navigation.map((item, index) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.href}
                      className={`
                        group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200
                        ${active
                          ? 'bg-white bg-opacity-20 text-white shadow-md'
                          : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10 hover:text-white'
                        }
                      `}
                      style={{ fontFamily: 'var(--font-family)' }}
                    >
                      <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${active ? 'text-white' : 'text-white text-opacity-70'}`} />
                      {item.name}
                    </Link>
                  </motion.div>
                );
              })}
              
              {/* Master Data Section */}
              <div className="mt-4 pt-4 border-t border-white border-opacity-20">
                <button
                  onClick={() => setMasterDataOpen(!masterDataOpen)}
                  className="w-full group flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg text-white text-opacity-80 hover:bg-white hover:bg-opacity-10 hover:text-white transition-all duration-200"
                  style={{ fontFamily: 'var(--font-family)' }}
                >
                  <div className="flex items-center">
                    <Grid className="mr-3 flex-shrink-0 h-5 w-5 text-white text-opacity-70" />
                    Master Data
                  </div>
                  {masterDataOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {masterDataOpen && (
                  <div className="mt-2 space-y-1 pl-4">
                    {masterDataItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`
                            group flex items-center px-4 py-2 text-xs font-medium rounded-lg transition-all duration-200
                            ${active
                              ? 'bg-white bg-opacity-20 text-white'
                              : 'text-white text-opacity-70 hover:bg-white hover:bg-opacity-10 hover:text-white'
                            }
                          `}
                          style={{ fontFamily: 'var(--font-family)' }}
                        >
                          <Icon className="mr-2 flex-shrink-0 h-4 w-4" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </nav>
          </div>
          
          {/* User Profile Section */}
          <div className="flex-shrink-0 flex border-t border-white border-opacity-20 p-4">
            <div className="flex items-center w-full">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center backdrop-blur-sm">
                  <UserCircle className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-white" style={{ fontFamily: 'var(--font-family)' }}>
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-white text-opacity-70" style={{ fontFamily: 'var(--font-family)' }}>
                  {user?.role || 'Staff'}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleLogout}
                className="ml-2 p-2 text-white text-opacity-70 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-all duration-200"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 md:hidden" 
            onClick={() => setSidebarOpen(false)} 
          />
          <motion.div 
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 left-0 flex flex-col max-w-xs w-full bg-gradient-to-b from-[var(--color-primary)] to-[var(--color-secondary)] shadow-2xl z-50 md:hidden"
          >
            <div className="absolute top-0 right-0 -mr-12 pt-4">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none bg-white bg-opacity-20"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4 mb-8">
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-family)' }}>
                  {companyName}
                </h1>
              </div>
              <nav className="px-3 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200
                        ${active
                          ? 'bg-white bg-opacity-20 text-white shadow-md'
                          : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10 hover:text-white'
                        }
                      `}
                      style={{ fontFamily: 'var(--font-family)' }}
                    >
                      <Icon className="mr-4 flex-shrink-0 h-6 w-6" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            
            <div className="flex-shrink-0 flex border-t border-white border-opacity-20 p-4">
              <div className="flex items-center w-full">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <UserCircle className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-white" style={{ fontFamily: 'var(--font-family)' }}>
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-white text-opacity-70" style={{ fontFamily: 'var(--font-family)' }}>
                    {user?.role || 'Staff'}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 p-2 text-white text-opacity-70 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-all"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* Main Content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top Bar: mobile menu + title, desktop spacer + notifications */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <button
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                  {companyName}
                </h1>
                <p className="text-sm text-black" style={{ fontFamily: 'var(--font-family)' }}>
                  BOQ & Quotation System
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" ref={notificationsRef}>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setNotificationsOpen((prev) => !prev)}
                  className="relative p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  title="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                {notificationsOpen && (
                  <div className="absolute right-0 mt-1 w-80 rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-50 max-h-[24rem] overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-900">Notifications</span>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={markAllRead}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {notifications.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-gray-500 text-center">No notifications</p>
                      ) : (
                        notifications.map((n) => (
                          <button
                            key={n._id}
                            type="button"
                            onClick={() => handleNotificationClick(n)}
                            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${!n.read ? 'bg-blue-50/50' : ''}`}
                          >
                            <p className="font-medium text-gray-900">{n.title}</p>
                            {n.message && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>}
                            <p className="text-[10px] text-gray-400 mt-1">
                              {new Date(n.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                            </p>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Page Content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
